import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useApi } from "~/api/provider";
import { useCurrentWorkspace, useViewer } from "~/api/queries";
import { ActionRequired } from "~/foundation/components/ActionRequired";
import { DecisionPopover } from "./bench/DecisionPopover";
import { actionCalls, groupFor, type WorkTodoData } from "./workTodo";

/** Shared by the console and drawer. Scope the cache to the person and workspace. */
export function useWorkspaceDecisions(poll = false) {
  const api = useApi();
  const { data: workspace } = useCurrentWorkspace();
  const { data: viewer } = useViewer();
  return useQuery({
    queryKey: ["work", "decisions", workspace?.id, viewer?.id],
    queryFn: async () => {
      const result = (await api.work.call("work.todo", { scope: "workspace" })) as WorkTodoData;
      if (!Array.isArray(result.entries)) throw new Error("The decision list could not be loaded.");
      return {
        ...result,
        entries: result.entries.filter((entry) => groupFor(entry) !== "elsewhere"),
      };
    },
    enabled: !!workspace && !!viewer,
    staleTime: 30_000,
    refetchInterval: poll ? 60_000 : false,
    retry: false,
  });
}

export function WorkspaceDecisions({ limit }: { limit?: number }) {
  const { t } = useTranslation();
  const api = useApi();
  const queryClient = useQueryClient();
  const query = useWorkspaceDecisions();
  const entries = query.data?.entries ?? [];
  return (
    <section aria-label={t("Workspace decisions")} className="space-y-4">
      <div>
        <h3 className="font-semibold">{t("Needs your decision")}</h3>
        <p className="mt-1 text-small text-text-secondary">
          {t("Choose the next step. Each button says what it will do.")}
        </p>
      </div>
      {query.error ? (
        <ActionRequired
          error={query.error.message}
          onRetry={() => void query.refetch()}
          busy={query.isFetching}
        />
      ) : null}
      {query.isPending ? (
        <p role="status" className="text-small">
          {t("Loading decisions…")}
        </p>
      ) : null}
      {!query.isPending && !query.error && entries.length === 0 ? (
        <p className="text-small text-text-secondary">
          {t("No work decisions are waiting in this workspace.")}
        </p>
      ) : null}
      {entries.slice(0, limit).map((entry) => {
        const actions = entry.actions.filter((a) => a.kind === "call" && a.call);
        return (
          <div key={entry.id} className="space-y-2">
            {actions.length ? (
              <DecisionPopover
                key={JSON.stringify(entry.actions)}
                inline
                question={entry.title}
                because={entry.explanation}
                answers={actions.map((a) => ({
                  id: a.id,
                  label: a.label,
                  description: a.description,
                  variant: a.danger ? "danger" : a.primary ? "primary" : "neutral",
                  needsText: a.requiresText,
                  acceptsText: a.acceptsText,
                }))}
                onPick={async (id, text) => {
                  const action = actions.find((a) => a.id === id);
                  if (!action) throw new Error("This choice is no longer available.");
                  for (const call of actionCalls(action, text ?? ""))
                    await api.work.call(
                      call.method,
                      entry.workspace.current
                        ? call.body
                        : { ...call.body, teamId: entry.workspace.teamId },
                    );
                }}
                onDone={() => void queryClient.invalidateQueries({ queryKey: ["work"] })}
              />
            ) : (
              <div className="text-small">
                <p className="font-medium">{entry.title}</p>
                <p className="text-text-secondary">{entry.explanation}</p>
              </div>
            )}
            <Link
              to={"/work/todo" as never}
              search={{ open: entry.id } as never}
              className="inline-block text-caption text-accent underline"
            >
              {t("Open work and all choices")}
            </Link>
          </div>
        );
      })}
      {limit && entries.length > limit ? (
        <Link to={"/work/todo" as never} className="inline-block text-small text-accent underline">
          {t("See all {{count}} decisions", { count: entries.length })}
        </Link>
      ) : null}
    </section>
  );
}
